import { FormEvent, useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";

import { leaveApi } from "../api";
import { useToast } from "#/components/common/ToastProvider";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { LeaveBackButton } from "../components/LeaveBackButton";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { LeavePageHeader } from "../components/LeavePageHeader";
import { LeaveSetupTabs } from "../components/LeaveSetupTabs";
import { formatDays } from "../hooks";
import { useAsyncData, useMutation } from "../hooks";
import type {
  EmployeeOption,
  LeaveDayCountMode,
  LeaveEntitlementRule,
  LeaveEntitlementRuleCreateRequest,
  LeaveRuleOption,
  LeaveType,
  RefLookup,
} from "../types";

/**
 * Fields that exist BOTH on a Leave Type (its org-wide default) and on a Leave Entitlement Rule
 * (an optional per-rule override). This is the single source of truth for that overlap — the
 * form, the submit payload, and the rules table all derive from this array instead of each
 * hand-coding the same five fields. Add a new shared field here once and it shows up everywhere.
 */
type RuleOverridableKey =
  | "dayCountMode"
  | "maxDaysPerRequest"
  | "requiresAttachment"
  | "excludeWeekends"
  | "excludePublicHolidays";

type RuleFieldSpec =
  | {
      key: "dayCountMode";
      label: string;
      kind: "select";
      options: { value: LeaveDayCountMode; label: string }[];
    }
  | { key: "maxDaysPerRequest"; label: string; kind: "number" }
  | {
      key: "requiresAttachment" | "excludeWeekends" | "excludePublicHolidays";
      label: string;
      kind: "boolean";
    };

const RULE_POLICY_FIELDS: RuleFieldSpec[] = [
  {
    key: "dayCountMode",
    label: "Day Count Mode",
    kind: "select",
    options: [
      { value: "WORKING_DAYS", label: "Working Days" },
      { value: "CALENDAR_DAYS", label: "Calendar Days" },
    ],
  },
  { key: "maxDaysPerRequest", label: "Max Days Per Request", kind: "number" },
  { key: "requiresAttachment", label: "Requires Attachment", kind: "boolean" },
  { key: "excludeWeekends", label: "Exclude Weekends", kind: "boolean" },
  { key: "excludePublicHolidays", label: "Exclude Public Holidays", kind: "boolean" },
];

function findRuleField(key: RuleOverridableKey): RuleFieldSpec {
  return RULE_POLICY_FIELDS.find((field) => field.key === key)!;
}

/**
 * A rule only needs a value here when it deliberately overrides the Leave Type's own default for
 * the group it targets. This looks up what "inherit" currently resolves to, so the form can show
 * it and pre-fill an override starting point instead of an admin having to know/retype it.
 */
function leaveTypeDefault(leaveType: LeaveType | undefined, key: RuleOverridableKey) {
  if (!leaveType) return null;
  switch (key) {
    case "dayCountMode":
      return leaveType.dayCountMode ?? "WORKING_DAYS";
    case "maxDaysPerRequest":
      return leaveType.maxDaysPerRequest ?? null;
    case "requiresAttachment":
      return leaveType.requiresAttachment ?? false;
    case "excludeWeekends":
      return leaveType.excludeWeekends ?? true;
    case "excludePublicHolidays":
      return leaveType.excludePublicHolidays ?? true;
    default:
      return null;
  }
}

function formatPolicyValue(spec: RuleFieldSpec, value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (spec.kind === "select") {
    return spec.options.find((option) => option.value === value)?.label ?? String(value);
  }
  if (spec.kind === "boolean") {
    return value ? "Yes" : "No";
  }
  return String(value);
}

/** Table display: null/undefined on the saved rule means it inherits from the Leave Type. */
function policyCell(spec: RuleFieldSpec, value: unknown): string {
  if (value === null || value === undefined) return "Inherited";
  return formatPolicyValue(spec, value);
}

/**
 * Builds the submit payload for every shared policy field from RULE_POLICY_FIELDS instead of
 * five hand-written coercions. A field left at "inherit" (null) is sent as null so the backend's
 * own fallback-to-Leave-Type logic applies; an overridden field is coerced to its proper type.
 */
function buildPolicyOverridePayload(
  form: LeaveEntitlementRuleCreateRequest,
): Pick<LeaveEntitlementRuleCreateRequest, RuleOverridableKey> {
  const result = {} as Pick<LeaveEntitlementRuleCreateRequest, RuleOverridableKey>;
  for (const spec of RULE_POLICY_FIELDS) {
    const value = form[spec.key];
    if (value === null || value === undefined || (value as unknown) === "") {
      (result as Record<string, unknown>)[spec.key] = null;
    } else if (spec.kind === "number") {
      (result as Record<string, unknown>)[spec.key] = Number(value);
    } else if (spec.kind === "boolean") {
      (result as Record<string, unknown>)[spec.key] = Boolean(value);
    } else {
      (result as Record<string, unknown>)[spec.key] = value;
    }
  }
  return result;
}

export default function LeaveEntitlementRulesPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<LeaveEntitlementRule | null>(null);
  const [showForm, setShowForm] = useState(false);

  const rules = useAsyncData(() => leaveApi.entitlementRules(), []);
  const leaveTypes = useAsyncData(() => leaveApi.allLeaveTypes(), []);
  const employees = useAsyncData(() => leaveApi.employeeOptions(), []);
  const genders = useAsyncData(() => leaveApi.genders(), []);
  const employmentTypes = useAsyncData(() => leaveApi.employmentTypes(), []);
  const employeeCategories = useAsyncData(() => leaveApi.employeeCategories(), []);
  const roles = useAsyncData(() => leaveApi.roles(), []);
  const departments = useAsyncData(() => leaveApi.departments(), []);

  const save = useMutation(async (payload: LeaveEntitlementRuleCreateRequest) => {
    if (editing) {
      return leaveApi.updateEntitlementRule(editing.id, payload);
    }

    return leaveApi.createEntitlementRule(payload);
  });

  async function handleSubmit(payload: LeaveEntitlementRuleCreateRequest) {
    await save.mutate(payload);
    setEditing(null);
    setShowForm(false);
    await rules.reload();
  }

  async function handleToggle(item: LeaveEntitlementRule) {
    const action = item.active ? "deactivate" : "activate";

    if (!(await confirm(`Are you sure you want to ${action} ${item.ruleName}?`))) {
      return;
    }

    if (item.active) {
      await leaveApi.deactivateEntitlementRule(item.id);
    } else {
      await leaveApi.activateEntitlementRule(item.id);
    }

    await rules.reload();
  }

  const loading =
    rules.loading ||
    leaveTypes.loading ||
    employees.loading ||
    genders.loading ||
    employmentTypes.loading ||
    employeeCategories.loading ||
    roles.loading ||
    departments.loading;

  return (
    <div className="space-y-5">
      <LeavePageHeader
        title="Entitlement Rules"
        actions={
          <div className="flex items-center gap-2">
            <LeaveBackButton to="/hr/leave/setup" label="Setup" />
            <button
              type="button"
              onClick={() => {
                setEditing(null);
                setShowForm(true);
              }}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800"
            >
              <Plus className="h-4 w-4" />
              New Rule
            </button>
          </div>
        }
      />

      <LeaveSetupTabs />

      {rules.error ? <LeaveError message={rules.error} onRetry={rules.reload} /> : null}
      {leaveTypes.error ? <LeaveError message={leaveTypes.error} /> : null}
      {employees.error ? <LeaveError message={employees.error} /> : null}
      {genders.error ? <LeaveError message={genders.error} /> : null}
      {employmentTypes.error ? <LeaveError message={employmentTypes.error} /> : null}
      {employeeCategories.error ? <LeaveError message={employeeCategories.error} /> : null}
      {roles.error ? <LeaveError message={roles.error} /> : null}
      {departments.error ? <LeaveError message={departments.error} /> : null}
      {save.error ? <LeaveError message={save.error} /> : null}

      {showForm || editing ? (
        <EntitlementRuleForm
          initialValue={editing}
          leaveTypes={leaveTypes.data || []}
          employees={employees.data || []}
          genders={genders.data || []}
          employmentTypes={employmentTypes.data || []}
          employeeCategories={employeeCategories.data || []}
          roles={roles.data || []}
          departments={departments.data || []}
          submitting={save.loading}
          onSubmit={handleSubmit}
          onCancel={() => {
            setEditing(null);
            setShowForm(false);
          }}
        />
      ) : null}

      {loading ? <LeaveLoading label="Loading entitlement rules..." /> : null}

      {rules.data ? (
        <EntitlementRulesTable
          items={rules.data}
          onEdit={(item) => {
            setEditing(item);
            setShowForm(true);
          }}
          onToggle={handleToggle}
        />
      ) : null}
    </div>
  );
}

function EntitlementRuleForm({
  initialValue,
  leaveTypes,
  employees,
  genders,
  employmentTypes,
  employeeCategories,
  roles,
  departments,
  submitting,
  onSubmit,
  onCancel,
}: {
  initialValue?: LeaveEntitlementRule | null;
  leaveTypes: LeaveType[];
  employees: EmployeeOption[];
  genders: RefLookup[];
  employmentTypes: RefLookup[];
  employeeCategories: RefLookup[];
  roles: LeaveRuleOption[];
  departments: LeaveRuleOption[];
  submitting?: boolean;
  onSubmit: (payload: LeaveEntitlementRuleCreateRequest) => Promise<void> | void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<LeaveEntitlementRuleCreateRequest>({
    leaveTypeId: 0,
    ruleName: "",
    employeeId: null,
    roleId: null,
    departmentId: null,
    employeeCategoryId: null,
    genderId: null,
    employmentTypeId: null,
    entitledDays: 0,
    maxDaysPerRequest: null,
    requiresAttachment: null,
    dayCountMode: null,
    excludeWeekends: null,
    excludePublicHolidays: null,
    priority: 100,
    active: true,
  });

  useEffect(() => {
    if (initialValue) {
      setForm({
        leaveTypeId: initialValue.leaveTypeId,
        ruleName: initialValue.ruleName,
        employeeId: initialValue.employeeId ?? null,
        roleId: initialValue.roleId ?? null,
        departmentId: initialValue.departmentId ?? null,
        employeeCategoryId: initialValue.employeeCategoryId ?? null,
        genderId: initialValue.genderId ?? null,
        employmentTypeId: initialValue.employmentTypeId ?? null,
        entitledDays: initialValue.entitledDays,
        maxDaysPerRequest: initialValue.maxDaysPerRequest ?? null,
        requiresAttachment: initialValue.requiresAttachment ?? null,
        dayCountMode: initialValue.dayCountMode ?? null,
        excludeWeekends: initialValue.excludeWeekends ?? null,
        excludePublicHolidays: initialValue.excludePublicHolidays ?? null,
        priority: initialValue.priority,
        active: initialValue.active,
      });
    }
  }, [initialValue]);

  const selectedLeaveType = leaveTypes.find((item) => item.id === form.leaveTypeId);

  const employeeLabel = useMemo(
    () => (item: EmployeeOption) =>
      item.employeeName ||
      item.fullName ||
      item.name ||
      `${item.employeeNumber}`,
    [],
  );

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (!form.leaveTypeId) {
      toast.error("Please select leave type.");
      return;
    }

    if (!form.ruleName.trim()) {
      toast.error("Please enter rule name.");
      return;
    }

    if (Number(form.entitledDays) < 0) {
      toast.error("Entitled days cannot be negative.");
      return;
    }

    await onSubmit({
      ...form,
      ruleName: form.ruleName.trim(),
      employeeId: form.employeeId || null,
      roleId: form.roleId || null,
      departmentId: form.departmentId || null,
      employeeCategoryId: form.employeeCategoryId || null,
      genderId: form.genderId || null,
      employmentTypeId: form.employmentTypeId || null,
      entitledDays: Number(form.entitledDays),
      ...buildPolicyOverridePayload(form),
      priority: Number(form.priority || 100),
      active: Boolean(form.active),
    });
  }

  function set<K extends keyof LeaveEntitlementRuleCreateRequest>(
    key: K,
    value: LeaveEntitlementRuleCreateRequest[K],
  ) {
    setForm((previous) => ({
      ...previous,
      [key]: value,
    }));
  }

  return (
    <form
      onSubmit={submit}
      className="grid gap-4 rounded-3xl border bg-white p-5 shadow-sm md:grid-cols-2"
    >
      <Field label="Leave Type" required>
        <select
          className="input"
          value={form.leaveTypeId || ""}
          onChange={(event) => set("leaveTypeId", Number(event.target.value))}
          required
        >
          <option value="">Select leave type</option>
          {leaveTypes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name} ({item.code})
            </option>
          ))}
        </select>
      </Field>

      <Field label="Rule Name" required>
        <input
          className="input"
          value={form.ruleName}
          onChange={(event) => set("ruleName", event.target.value)}
          placeholder="Normal staff annual leave"
          required
        />
      </Field>

      <Field label="Employee Category">
        <select
          className="input"
          value={form.employeeCategoryId ?? ""}
          onChange={(event) =>
            set(
              "employeeCategoryId",
              event.target.value ? Number(event.target.value) : null,
            )
          }
        >
          <option value="">Any category</option>
          {employeeCategories.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Gender">
        <select
          className="input"
          value={form.genderId ?? ""}
          onChange={(event) =>
            set("genderId", event.target.value ? Number(event.target.value) : null)
          }
        >
          <option value="">Any gender</option>
          {genders.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Employment Type">
        <select
          className="input"
          value={form.employmentTypeId ?? ""}
          onChange={(event) =>
            set(
              "employmentTypeId",
              event.target.value ? Number(event.target.value) : null,
            )
          }
        >
          <option value="">Any employment type</option>
          {employmentTypes.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Specific Employee">
        <select
          className="input"
          value={form.employeeId ?? ""}
          onChange={(event) =>
            set("employeeId", event.target.value ? Number(event.target.value) : null)
          }
        >
          <option value="">Any employee</option>
          {employees.map((item) => (
            <option key={item.id} value={item.id}>
              {employeeLabel(item)} - {item.employeeNumber}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Role">
        <select
          className="input"
          value={form.roleId ?? ""}
          onChange={(event) =>
            set("roleId", event.target.value ? Number(event.target.value) : null)
          }
        >
          <option value="">Any role</option>
          {roles.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Department">
        <select
          className="input"
          value={form.departmentId ?? ""}
          onChange={(event) =>
            set("departmentId", event.target.value ? Number(event.target.value) : null)
          }
        >
          <option value="">Any department</option>
          {departments.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Entitled Days" required>
        <input
          type="number"
          min={0}
          step="0.5"
          className="input"
          value={form.entitledDays}
          onChange={(event) => set("entitledDays", Number(event.target.value))}
          required
        />
      </Field>

      <div className="md:col-span-2 grid gap-3 sm:grid-cols-2">
        {RULE_POLICY_FIELDS.map((spec) => (
          <PolicyOverrideField
            key={spec.key}
            spec={spec}
            form={form}
            set={(key, value) => set(key, value)}
            leaveType={selectedLeaveType}
          />
        ))}
      </div>

      <Field label="Priority">
        <input
          type="number"
          min={1}
          className="input"
          value={form.priority ?? 100}
          onChange={(event) => set("priority", Number(event.target.value))}
        />
      </Field>

      <Check
        label="Active"
        checked={Boolean(form.active)}
        onChange={(value) => set("active", value)}
      />

      <div className="flex justify-end gap-2 md:col-span-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50"
        >
          Cancel
        </button>

        <button
          type="submit"
          disabled={submitting}
          className="rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800 disabled:opacity-60"
        >
          {submitting
            ? "Saving..."
            : initialValue
              ? "Update Rule"
              : "Create Rule"}
        </button>
      </div>
    </form>
  );
}

function EntitlementRulesTable({
  items,
  onEdit,
  onToggle,
}: {
  items: LeaveEntitlementRule[];
  onEdit: (item: LeaveEntitlementRule) => void;
  onToggle: (item: LeaveEntitlementRule) => void;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Rule</th>
              <th className="px-4 py-3">Leave Type</th>
              <th className="px-4 py-3">Applies To</th>
              <th className="px-4 py-3">Days</th>
              <th className="px-4 py-3">Counting</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-bold text-slate-900">
                  {item.ruleName}
                </td>

                <td className="px-4 py-3">
                  <div className="font-semibold">{item.leaveTypeName}</div>
                  <div className="text-xs text-slate-500">
                    {item.leaveTypeCode}
                  </div>
                </td>

                <td className="px-4 py-3 text-xs text-slate-600">
                  <div>Category: {item.employeeCategoryName || "Any"}</div>
                  <div>Gender: {item.genderName || "Any"}</div>
                  <div>Type: {item.employmentTypeName || "Any"}</div>
                  <div>Role: {item.roleName || "Any"}</div>
                  <div>Department: {item.departmentName || "Any"}</div>
                  <div>Employee: {item.employeeName || "Any"}</div>
                </td>

                <td className="px-4 py-3">
                  <div className="font-black text-emerald-700">
                    {formatDays(item.entitledDays)}
                  </div>
                  <div className="text-xs text-slate-500">
                    Max: {policyCell(findRuleField("maxDaysPerRequest"), item.maxDaysPerRequest)}
                  </div>
                </td>

                <td className="px-4 py-3 text-xs text-slate-600">
                  <div>{policyCell(findRuleField("dayCountMode"), item.dayCountMode)}</div>
                  <div>
                    Weekends:{" "}
                    {item.excludeWeekends == null
                      ? "Inherited"
                      : item.excludeWeekends
                        ? "Excluded"
                        : "Counted"}
                  </div>
                  <div>
                    Holidays:{" "}
                    {item.excludePublicHolidays == null
                      ? "Inherited"
                      : item.excludePublicHolidays
                        ? "Excluded"
                        : "Counted"}
                  </div>
                </td>

                <td className="px-4 py-3">{item.priority}</td>

                <td className="px-4 py-3">
                  <Status active={item.active} />
                </td>

                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(item)}
                      className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                    >
                      Edit
                    </button>

                    <button
                      type="button"
                      onClick={() => onToggle(item)}
                      className="rounded-xl border px-3 py-1.5 text-xs font-bold hover:bg-slate-50"
                    >
                      {item.active ? "Deactivate" : "Activate"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {!items.length ? (
              <tr>
                <td
                  colSpan={8}
                  className="px-4 py-10 text-center text-slate-500"
                >
                  No entitlement rules configured.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Status({ active }: { active: boolean }) {
  return (
    <span
      className={`rounded-full px-3 py-1 text-xs font-bold ${
        active
          ? "bg-emerald-100 text-emerald-700"
          : "bg-slate-200 text-slate-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2">
      <div className="text-sm font-semibold text-slate-700">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </div>
      {children}
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-3 rounded-2xl border p-4 text-sm font-semibold text-slate-700">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4"
      />
      {label}
    </label>
  );
}

/**
 * One shared field on a rule that may either inherit its value live from the selected Leave
 * Type, or explicitly override it. Rendered once per entry in RULE_POLICY_FIELDS — this is the
 * only place that knows how to draw a select/number/boolean override field, so a new shared
 * field just needs a RULE_POLICY_FIELDS entry, not a new hand-built block here.
 */
function PolicyOverrideField({
  spec,
  form,
  set,
  leaveType,
}: {
  spec: RuleFieldSpec;
  form: LeaveEntitlementRuleCreateRequest;
  set: (key: RuleOverridableKey, value: LeaveEntitlementRuleCreateRequest[RuleOverridableKey]) => void;
  leaveType?: LeaveType;
}) {
  const current = form[spec.key];
  const overridden = current !== null && current !== undefined;
  const inherited = leaveTypeDefault(leaveType, spec.key);

  function toggleOverride(next: boolean) {
    if (!next) {
      set(spec.key, null);
      return;
    }
    if (spec.kind === "boolean") {
      set(spec.key, Boolean(inherited));
    } else if (spec.kind === "select") {
      set(spec.key, (inherited as LeaveDayCountMode) ?? spec.options[0].value);
    } else {
      set(spec.key, (inherited as number | null) ?? null);
    }
  }

  return (
    <div className="rounded-2xl border p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-700">{spec.label}</div>
        <label className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
          <input
            type="checkbox"
            className="h-3.5 w-3.5"
            checked={overridden}
            disabled={!leaveType}
            onChange={(event) => toggleOverride(event.target.checked)}
          />
          Override
        </label>
      </div>

      {!overridden ? (
        <p className="mt-2 text-sm text-slate-500">
          {leaveType ? (
            <>
              Inherited from leave type:{" "}
              <span className="font-semibold text-slate-700">
                {formatPolicyValue(spec, inherited)}
              </span>
            </>
          ) : (
            "Select a leave type first"
          )}
        </p>
      ) : spec.kind === "select" ? (
        <select
          className="input mt-2"
          value={String(current ?? spec.options[0].value)}
          onChange={(event) => set(spec.key, event.target.value as LeaveDayCountMode)}
        >
          {spec.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : spec.kind === "number" ? (
        <input
          type="number"
          min={0}
          step="0.5"
          className="input mt-2"
          value={current == null ? "" : Number(current)}
          onChange={(event) =>
            set(spec.key, event.target.value ? Number(event.target.value) : null)
          }
        />
      ) : (
        <label className="mt-2 flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={Boolean(current)}
            onChange={(event) => set(spec.key, event.target.checked)}
          />
          {spec.label}
        </label>
      )}
    </div>
  );
}